import { Injectable, EventEmitter } from '@angular/core';
import { Http, Headers, RequestOptions } from '@angular/http';
import { UUID } from 'angular2-uuid';
// import * as jsSHA from 'jssha';
import { Storage } from '@ionic/storage';
import { ConfigService } from "./config.service";

declare let jsSHA: any;

/**
 * States the AuthService can be in.
 */
enum AuthServiceState {
  UNINITIALIZED,
  ANONYMOUS,
  REGISTERED
}

/**
 * Configuration that has to be passed to the Service after creating it.
 */
export interface IConfig {
  baseUrl: string,
  baseVersion: string,
  anonymousDomain: string
  debug?: boolean
}

export interface Profile {
  api_key: string;
  creationTimestamp: Date;
  date_joined: Date;
  email: string;
  first_name: string;
  is_student: boolean;
  language: string;
  last_name: string;
  modificationTimestamp: Date;
  resource_uri: string;
  syncStatus: number;
  username: string;
  uuid: string;
}

@Injectable()
export class AuthService {
  private profile: Profile;
  private state: AuthServiceState;

  emitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  config: IConfig;

  constructor(private http: Http, private storage: Storage, public _config: ConfigService) {
    this.config = {
      baseUrl: this._config.baseUrl + '/api/',
      baseVersion: this._config.baseVersion,
      anonymousDomain: this._config.anonymousDomain,
      debug: true
    };
    this.state = AuthServiceState.UNINITIALIZED;
    this.http = http;

    this.storage.get('userResponse').then(res => {
      this.profile = res;
    })
  }

  setConfig(config: IConfig) {
    if (!config.debug) {
      config.debug = false;
    }
    this.config = config;

    this.log('fn: setConfig');
  }

  getProfile() {
    return this.profile;
  }

  setProfile(profile) {
    this.profile = profile;
  }

  /**
   * Generates the AuthHeader used for Authentication (when trying to log in)
   * This Header (Basic Auth) is used when accessing the ApiUser itself
   *
   * @param email
   * @param password
   */
  private generateAuthHeader(email: string, password: string): Promise<any> {
    this.log('fn: generateAuthHeader: ' + email + ' ' + password);

    let shaObj = new jsSHA("SHA-256", "TEXT");
    shaObj.update(email);
    let hashedEmail = shaObj.getHash("HEX").substring(0, 30);
    let basicHeader = 'Basic ' + btoa(hashedEmail + ':' + password);
    return this.setAuthHeader(basicHeader);
  }

  private setAuthHeader(basicHeader): Promise<any> {
    this.log('fn: setAuthHeader');

    this.log('Setting new AuthHeader: ', basicHeader);
    return this.storage.set('authHeader', basicHeader);
  }

  getAuthHeader(): Promise<any> {
    this.log('fn: getAuthHeader');

    return this.storage.get('authHeader');
  }

  /**
   * This Header is used for API calls
   *
   * @param username
   * @param api_key
   */
  public generateRequestHeader(username: string, api_key: string): Promise<any> {
    this.log('fn: generateRequestHeader');

    return Promise.all([
      this.storage.set('username', username),
      this.storage.set('api_key', api_key),
      this.storage.set('requestHeader', 'ApiKey ' + username + ':' + api_key)
    ]);
  }

  private getRequestHeader(): Promise<any> {
    this.log('fn: getRequestHeader');

    return this.storage.get('requestHeader');
  }

  /**
   * Return RequestOptions used for API calls
   *
   * @returns {RequestOptions}
   */
  private getRequestOptions() {
    this.log('fn: getRequestOptions');

    return new Promise((resolve, reject) => {
      this.getRequestHeader().then(header => {
        let headers = new Headers({ 'Content-Type': 'application/json', 'Authorization': header });
        this.log("HAVE HEADERS: ", new RequestOptions({ headers: headers }));
        resolve(new RequestOptions({ headers: headers }));
      }).catch(reject);
    });
  }

  public createUserWithCredentials(email, password, passwordVerify) {

    let newUser = {
      email,
      password
    };

    let body = JSON.stringify(newUser);
    let headers = new Headers({ 'Content-Type': 'application/json' });
    let options = new RequestOptions({ headers: headers });

    return new Promise((resolve, reject) => {
      if (password === passwordVerify) {
        this.http.post(this.config.baseUrl + this.config.baseVersion + '/apiUser/register/', body, options)
          .subscribe((data) => {
            this.log('CreateNewUser response:', data);
            this.profile = data.json();

            this.generateAuthHeader(newUser.email, newUser.password).then(() => {
              this.state = AuthServiceState.REGISTERED;
              this.emitter.emit(true);
              resolve();
            }).catch(reject);
          }, reject);
      } else {
        reject('Passwords do not match!');
      }
    });
  }

  /**
   * Make changes to the ApiUser Object
   * Set state to REGISTERED
   *
   * @param userObj
   * @returns {Promise<T>|Promise}
   */
  register(userObj): Promise<any> {
    this.log('fn: register');

    return new Promise((resolve, reject) => {

      if (userObj.email && !userObj.password) {
        return reject('Password needs to be provided when changing the email');
      }


      this.getAuthHeader().then(header => {

        let headers = new Headers({ 'Content-Type': 'application/json', 'Authorization': header });
        let options = new RequestOptions({ headers: headers });

        let putRequest = (userObj) => {
          this.http.put(this.config.baseUrl + this.config.baseVersion + '/apiUser/' + userObj.uuid + '/', userObj, options)
            .subscribe(data => {
              this.profile = data.json();

              let success = () => {
                this.generateRequestHeader(data.json().username, data.json().api_key).then(() => {
                  if (this.state != AuthServiceState.REGISTERED) {
                    this.state = AuthServiceState.REGISTERED;
                    this.emitter.emit(true);
                  }
                  resolve(data);

                }).catch(reject);
              }

              // We need to regenerate Headers because the values might have changed
              if (userObj.password) {
                this.generateAuthHeader(data.json().email, userObj.password)
                  .then(success)
                  .catch(reject);
              } else {
                success();
              }

            }, error => reject(error));
        }

        if (!userObj.uuid) {
          this.storage.get('userResponse').then(obj => {
            userObj.uuid = obj.uuid;
            putRequest(userObj);
          }).catch(reject);
        } else {
          putRequest(userObj);
        }
      }).catch(reject);

    });
  }

  /**
   * Gets Infos about the current ApiUser, sets the state of the service to anonymous and generates the RequestHeaders
   *
   * @returns {Promise<T>|Promise}
   */
  getUserInfoWithAuthHeader(): Promise<any> {
    this.log('fn: getUserInfo');

    return new Promise((resolve, reject) => {

      //this.getRequestHeader().then(requestHeader)
      this.getAuthHeader().then(header => {
        let headers = new Headers({ 'Content-Type': 'application/json', 'Authorization': header });
        let options = new RequestOptions({ headers: headers });

        this.http.get(this.config.baseUrl + this.config.baseVersion + '/apiUser/me/', options)
          .subscribe(data => {
            this.profile = data.json();

            this.log('AuthModule: getUserInfo:', data.json());
            this.storage.set('userResponse', data.json()).then(() => {
              this.generateRequestHeader(data.json().username, data.json().api_key).then(() => {
                resolve(data.json());
              }).catch(reject);
            }).catch(reject);

          }, error => reject(error));

      })
    });
  }

  /**
   * Gets Infos about the current ApiUser, sets the state of the service to anonymous and generates the RequestHeaders
   *
   * @returns {Promise<T>|Promise}
   */
  getUserInfo(): Promise<any> {
    this.log('fn: getUserInfo');

    return new Promise((resolve, reject) => {

      this.getRequestHeader().then(header => {
        if (header) {
          let headers = new Headers({ 'Content-Type': 'application/json', 'Authorization': header });
          let options = new RequestOptions({ headers: headers });

          this.http.get(this.config.baseUrl + this.config.baseVersion + '/apiUser/me/', options)
            .subscribe(data => {
              this.profile = data.json();

              this.log('AuthModule: getUserInfo:', data.json());
              this.storage.set('userResponse', data.json()).then(() => {
                resolve(data.json());
              }).catch(reject);

            }, error => reject(error));
        } else {
          reject({ message: 'Not logged in' });
        }
      })
    });
  }

  /**
   * Sets the email and password of a temporary user
   *
   * @param email
   * @param password
   * @returns {Promise<T>|Promise}
   */
  signup(email: string, password: string): Promise<any> {
    this.log('fn: signup');

    return this.register({ email: email, password: password });
  }

  /**
   * Sets the AuthHeader (Basic Auth) to the provided email/password and tries to authenticate.
   *
   * @param email
   * @param password
   * @returns {Promise<T>|Promise}
   */
  login(email: string, password: string): Promise<any> {
    this.log('fn: login' + email + ' ' + password);

    return new Promise((resolve, reject) => {
      this.getAuthHeader().then(tempAuthHeader => { // Save the current header in case authentication fails
        this.generateAuthHeader(email, password).then(() => {
          this.getUserInfoWithAuthHeader()
            .then(data => {
              resolve(data);
            })
            .catch(err => {
              this.setAuthHeader(tempAuthHeader).then(() => {
                reject(err); // Replace header with old, anonymous credentials
              }).catch(reject);
            });
        }).catch(reject);
      });
    });
  }

  /**
   * Log the user out and delete all local references
   * Set state to UNINITIALIZED
   *
   * @returns {Promise<T>|Promise}
   */
  logout(): Promise<any> {
    this.log('fn: logout');

    return Promise.all([
      this.storage.remove('authHeader'),
      this.storage.remove('username'),
      this.storage.remove('api_key'),
      this.storage.remove('requestHeader'),
      this.storage.remove('userResponse'),
    ]).then(() => {
      this.profile = null;
      this.state = AuthServiceState.UNINITIALIZED;
      this.emitter.emit(true);

      return Promise.resolve();
    });
  }

  /**
   * Get request to an endpoint with all the required headers set automatically
   *
   * @param endpoint
   * @param parameters
   * @returns {Promise<T>|Promise}
   */
  get(endpoint, parameters = ''): Promise<any> {
    this.log('fn: get');

    return new Promise((resolve, reject) => {
      this.getRequestOptions()
        .then(options => {
          this.log("Options: ", options);
          this.http.get(this.config.baseUrl + this.config.baseVersion + '/' + endpoint + '/' + parameters, options)
            .subscribe(data => {
              // wait until not uninit
              try {
                this.log('get response', data.json());
                resolve(data.json());
              } catch (e) {
                reject(e);
              }
            }, error => {
              reject(error);
            });
        })
        .catch(reject);
    });
  }

  /**
   * Puts an object to an endpoint with all the required headers set automatically
   *
   * @param endpoint
   * @param object
   * @returns {Promise<T>|Promise}
   */
  put(endpoint, object): Promise<any> {
    this.log('fn: put');

    return new Promise((resolve, reject) => {
      this.getRequestOptions()
        .then(options => {
          this.http.put(this.config.baseUrl + this.config.baseVersion + '/' + endpoint + '/', object, options)
            .subscribe(data => {
              try { // TODO: Is there a better way to do this?
                this.log('put response', data.json());
                resolve(data.json());
              } catch (e) {
                this.log('put response');
                resolve();
              }
            }, error => {
              reject(error);
            });
        })
        .catch(reject);

    });
  }

  /**
   * Posts an object to an endpoint with all the required headers set automatically
   *
   * @param endpoint
   * @param object
   * @returns {Promise<T>|Promise}
   */
  post(endpoint, object): Promise<any> {
    this.log('fn: post');

    return new Promise((resolve, reject) => {
      this.getRequestOptions()
        .then(options => {
          this.http.post(this.config.baseUrl + this.config.baseVersion + '/' + endpoint + '/', object, options)
            .subscribe(data => {
              try {
                this.log('post response', data.json());
                resolve(data.json());
              } catch (e) {
                reject(e);
              }
            }, error => {
              reject(error);
            });
        })
        .catch(reject);

    });
  }

  /**
   * Deletes an object
   *
   * @param endpoint
   * @returns {Promise<T>|Promise}
   */
  delete(endpoint): Promise<any> {
    this.log('fn: delete');

    return new Promise((resolve, reject) => {
      this.getRequestOptions()
        .then(options => {
          this.http.delete(this.config.baseUrl + this.config.baseVersion + '/' + endpoint + '/', options)
            .subscribe(data => {
              //this.log('delete response', data.json());
              //resolve(data.json());
              resolve();
            }, error => {
              reject(error);
            });
        })
        .catch(reject);

    });
  }

  log(message, ...object) {
    if (this.config.debug) console.log('AUTH: ' + message, ...object);
  }
}



// WEBPACK FOOTER //
// ./src/providers/auth.service.ts